import { observer } from "mobx-react-lite";
import { ChevronDown, Clock, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import Progress from "@/components/common/Progress";
import { useTran } from "@/hooks";
import { showToast } from "@/lib/utils/toast";
import {
  useESGCollaborationStore,
  useESGReportBuilderStore,
  useESGDataEntryStore,
  useESGDisclosureSelectionStore,
  useESGReportWorkflowStore,
} from "@/features/esg/hooks/useESGReportStore";
import { exportESGReportToExcel } from "@/features/esg/utils/exportToExcel";
import { exportESGReportToWord } from "@/features/esg/services/report-export.service";

const ReviewExportCard = observer(() => {
  const { t } = useTran();
  const {
    progressPercentage,
    completedCount,
    entriesByCode,
    isDisclosureCompleted,
  } = useESGDataEntryStore();
  const {
    filteredSelectedCodesByCategory,
    availableDisclosures,
    customDisclosures,
    metricMappings,
  } = useESGDisclosureSelectionStore();
  const { setShowPreviewModal, goToStep, reviewExportFramework } =
    useESGReportWorkflowStore();
  const {
    reportName,
    framework,
    reportYear,
    orgName,
    notes,
    includeCover,
    includeToc,
    includeAppendix,
  } = useESGReportBuilderStore();
  const { delegatedDisclosures } = useESGCollaborationStore();

  const totalSelected = Object.values(filteredSelectedCodesByCategory).flat()
    .length;
  const pendingCount = Math.max(totalSelected - completedCount, 0);
  const isReady = progressPercentage === 100 && totalSelected > 0;

  const handleExport = (type) => {
    if (type === "Excel") {
      if (totalSelected === 0) {
        showToast.warning(t("Select disclosures before exporting."));
        return;
      }
      try {
        const fileName = exportESGReportToExcel({
          reportName,
          framework,
          reportYear,
          orgName,
          progressPercentage,
          completedCount,
          selectedCodesByCategory: filteredSelectedCodesByCategory,
          availableDisclosures,
          customDisclosures,
          entriesByCode,
          isDisclosureCompleted,
          delegatedDisclosures,
          exportFramework: reviewExportFramework,
          metricMappings,
          t,
        });
        showToast.success(t("Exported {{fileName}}", { fileName }));
      } catch (error) {
        showToast.error(error?.message || t("Excel export failed."));
      }
      return;
    }
    if (type === "Word") {
      if (totalSelected === 0) {
        showToast.warning(t("Select disclosures before exporting."));
        return;
      }
      showToast.promise(
        exportESGReportToWord({
          reportName,
          framework,
          reportYear,
          orgName,
          notes,
          includeCover,
          includeToc,
          includeAppendix,
          selectedCodesByCategory: filteredSelectedCodesByCategory,
          availableDisclosures,
          customDisclosures,
          entriesByCode,
          isDisclosureCompleted,
          progressPercentage,
          exportFramework: reviewExportFramework,
          metricMappings,
          t,
        }),
        {
          loading: t("Exporting Word document..."),
          success: (fileName) => t("Exported {{fileName}}", { fileName }),
          error: t("Word export failed."),
        },
      );
    }
  };

  return (
    <div className="h-full rounded-radius border border-border bg-card p-5 shadow-sm">
      <div className="grid grid-cols-[1fr_auto] items-start">
        <div>
          <p className="text-xs text-muted-foreground">
            {t("Export your ESG report")}
          </p>
          <h3 className="mt-0.5 text-lg font-semibold text-foreground">
            {t("Review and generate final outputs")}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(
              "PDF export is available at 100% completion. Excel and Word can be exported anytime.",
            )}
          </p>
        </div>
        {!isReady && (
          <Badge className="border border-amber-500/30 bg-amber-500/10 text-amber-600 text-xs">
            {t("Action needed")}
          </Badge>
        )}
      </div>

      <div className="mt-4 rounded-radius border border-border bg-background p-4">
        <div className="flex items-center flex-wrap justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">
              {t("Progress")}
            </p>
            <Badge className="border border-blue-500/30 bg-blue-500/10 text-blue-600 text-xs">
              {completedCount} {t("of")} {totalSelected} {t("complete")} (
              {progressPercentage}%)
            </Badge>
          </div>
          <div className="flex items-center gap-2 justify-self-end">
            {progressPercentage !== 100 && (
              <Button size="sm" variant="outline" onClick={() => goToStep(3)}>
                <Clock className="h-3.5 w-3.5" />
                {t("Go to pending")}
              </Button>
            )}
            <Button
              size="sm"
              className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-muted disabled:text-muted-foreground"
              onClick={() => isReady && setShowPreviewModal(true)}
              disabled={!isReady}
            >
              {t("Generate PDF")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  {t("Export")}
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExport("Excel")}>
                  <FileSpreadsheet className="h-4 w-4" />
                  <div>
                    <p className="text-sm">{t("Export to Excel")}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("Spreadsheet format (.xlsx)")}
                    </p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("Word")}>
                  <FileText className="h-4 w-4" />
                  <div>
                    <p className="text-sm">{t("Export to Word")}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("Document format (.docx)")}
                    </p>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mt-3">
          <Progress value={progressPercentage} />
        </div>

        <div className="mt-2 grid grid-cols-2">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <p className="text-xs text-muted-foreground">
              {t("Completed")}: {completedCount}
            </p>
          </div>
          <div className="flex items-center gap-1.5 justify-self-end">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            <p className="text-xs text-muted-foreground">
              {t("Pending")}: {pendingCount}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ReviewExportCard;
